import {
  ActionSheetButton,
  ActionSheetController,
  NavController,
} from 'ionic-angular';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { CustomMapMarker } from '../../../../providers/map/custom-map-marker';
import { DirectionsService } from '../../../../providers/directions/directions';
import { OpsOrderModel } from '../../../../models/order.ops';
import { OrderNavigationPage } from './../../../order-navigation/order-navigation';
import { YoshiMapStyle } from './../../../../theme/map-style';

@Component({
  selector: 'order-detail-location-card',
  templateUrl: 'order-detail-location-card.html',
})
export class OrderDetailLocationCardComponent implements OnDestroy, OnChanges {
  private readonly MIN_PARKING_DISTANCE = 10; // in meters
  private readonly GEOLOCATION_MARKER_ICON = 'assets/images/geo-pin.png';
  private readonly GEOLOCATION_MARKER_ANCHOR_OFFSET = { x: -25, y: -60 };

  map: google.maps.Map;
  addressMarker: google.maps.Marker;
  parkingSpotMarker: google.maps.Marker;
  initialCoords: google.maps.LatLng;
  parkingCoords: google.maps.LatLng;
  deliveryNote: string;
  geolocationMarker: CustomMapMarker;
  geolocationCoords: google.maps.LatLng;
  geolocationCreated: Date;

  @Input() order: OpsOrderModel;

  constructor(
    private navCtrl: NavController,
    private actionSheetCtlr: ActionSheetController,
    private directions: DirectionsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.order) {
      return;
    }
    const deliveryLocation = this.order.deliveryLocation;
    this.initialCoords = new google.maps.LatLng(
      this.order.address.lat,
      this.order.address.lng
    );
    this.parkingCoords =
      deliveryLocation &&
      new google.maps.LatLng(deliveryLocation.lat, deliveryLocation.lng);

    this.geolocationCoords =
      this.order.vehicle.geolocation &&
      new google.maps.LatLng(
        this.order.vehicle.geolocation.lat,
        this.order.vehicle.geolocation.lng
      );

    this.geolocationCreated =
      this.order.vehicle.geolocation &&
      this.order.vehicle.geolocation.createdAt;

    !this.map && this.initMap();
    this.setMarkers();
    if (deliveryLocation && deliveryLocation.details) {
      this.deliveryNote = `"${this.order.deliveryLocation.details}"`;
    }
  }

  ngOnDestroy() {
    this.map && google.maps.event.clearInstanceListeners(this.map);
  }

  // Map

  initMap() {
    let mapOptions: google.maps.MapOptions = {
      zoom: 15,
      tilt: 0,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      disableDefaultUI: true,
      styles: YoshiMapStyle,
      draggable: false,
      zoomControl: false,
      disableDoubleClickZoom: true,
    };
    let mapContainer = document.getElementById('order-detail-map');
    if (!mapContainer) {
      return;
    }
    this.resizeLocationWidget(mapContainer);
    this.map = new google.maps.Map(mapContainer, mapOptions);
    this.addressMarker = new google.maps.Marker({
      icon: 'assets/images/map-marker-flag.png',
      position: this.initialCoords,
    });
    this.parkingSpotMarker = new google.maps.Marker({
      icon: {
        url: 'assets/images/car-pin.png',
        scaledSize: new google.maps.Size(50, 67),
      },
      position: this.parkingCoords || this.initialCoords,
    });
  }

  setMarkers() {
    this.addressMarker && this.addressMarker.setMap(null);
    this.parkingSpotMarker && this.parkingSpotMarker.setMap(null);
    this.geolocationMarker && this.geolocationMarker.setMap(null);
    this.map.setCenter(this.initialCoords);
    this.addressMarker.setPosition(this.initialCoords);
    this.addressMarker.setMap(this.map);

    let bounds = new google.maps.LatLngBounds();
    let boundsSet: boolean = false;

    if (this.geolocationCoords) {
      this.geolocationMarker = new CustomMapMarker();
      this.geolocationMarker.init(
        this.geolocationCoords,
        this.GEOLOCATION_MARKER_ICON,
        new google.maps.Size(50, 67),
        this.geolocationCreated,
        this.GEOLOCATION_MARKER_ANCHOR_OFFSET.x,
        this.GEOLOCATION_MARKER_ANCHOR_OFFSET.y
      );
      this.geolocationMarker.setPosition(this.geolocationCoords);
      this.geolocationMarker.setMap(this.map);
      bounds.extend(this.geolocationMarker.getPosition());
      boundsSet = true;
    }
    if (this.parkingCoords) {
      this.parkingSpotMarker.setPosition(this.parkingCoords);
      this.parkingSpotMarker.setMap(this.map);
      bounds.extend(this.parkingSpotMarker.getPosition());
      bounds.extend(this.addressMarker.getPosition());
      boundsSet = true;
    }
    if (boundsSet) {
      this.map.fitBounds(bounds);
    }
  }

  // Actions

  didTapLocationCard() {
    const buttons: ActionSheetButton[] = [];
    buttons.push({
      text: 'Navigate to street address',
      handler: () =>
        this.directions.get(this.order.address.lat, this.order.address.lng),
    });
    if (this.parkingCoords) {
      buttons.unshift({
        text: 'Open full screen view of location',
        handler: () => this.openFullScreenMap(),
      });
      buttons.push({
        text: 'Navigate to customer’s pin',
        handler: () =>
          this.directions.get(
            this.parkingCoords.lat(),
            this.parkingCoords.lng()
          ),
      });
    }
    if (this.geolocationCoords) {
      buttons.push({
        text: 'Navigate to connected car location',
        handler: () =>
          this.directions.get(
            this.geolocationCoords.lat(),
            this.geolocationCoords.lng()
          ),
      });
    }
    buttons.push({
      text: 'Cancel',
      role: 'cancel',
    });
    let actionSheet = this.actionSheetCtlr.create({ buttons });
    actionSheet.present();
  }

  // Navigation

  openFullScreenMap() {
    if (!this.parkingCoords) {
      return;
    }
    this.navCtrl.push(OrderNavigationPage, {
      parkingCoords: this.parkingCoords,
      geolocationCoords: this.geolocationCoords,
      geolocationCreated: this.geolocationCreated,
      deliveryNote: this.deliveryNote,
      addressCoordinates: this.initialCoords,
    });
  }

  // Helpers

  showLocationCard() {
    if (!this.initialCoords || !this.parkingCoords) {
      return false;
    }
    return (
      google.maps.geometry.spherical.computeDistanceBetween(
        this.initialCoords,
        this.parkingCoords
      ) > this.MIN_PARKING_DISTANCE
    );
  }

  resizeLocationWidget(mapContainer: HTMLElement) {
    // ensures location card has same height and width
    const locationCard: HTMLElement = document.getElementById('location-card');
    const cardHeight = locationCard.offsetHeight;
    const cardWidth = locationCard.offsetWidth;
    const delta = cardWidth - cardHeight;
    const mapContainerHeight = mapContainer.offsetHeight;
    mapContainer.style.height = `${mapContainerHeight + delta}px`;
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-detail/components/order-detail-location-card/order-detail-location-card.ts